import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { getPage, updatePage, listPages } from '../lib/api';
import { ArrowLeft, Save } from 'lucide-react';

const PAGE_LIST = [
  { slug: 'home', label: 'Home' },
  { slug: 'about', label: 'About' },
  { slug: 'services', label: 'Services (hero)' },
  { slug: 'industries', label: 'Industries (hero)' },
  { slug: 'standards', label: 'Standards (hero)' },
  { slug: 'why-aqc', label: 'Why AQC (hero)' },
  { slug: 'resources', label: 'Resources (hero)' },
  { slug: 'contact', label: 'Contact (hero)' },
];

export default function PagesEditor() {
  const [editing, setEditing] = useState(null);

  if (editing) return <PageForm slug={editing} onBack={() => setEditing(null)} />;

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <h2 className="font-serif text-2xl text-aqc-navy">Pages & Content</h2>
      <p className="text-sm text-aqc-muted mt-1">Edit hero copy and call-to-action text for each public page.</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {PAGE_LIST.map((p) => (
          <button key={p.slug} onClick={() => setEditing(p.slug)} className="text-left px-4 py-3 rounded-md border border-aqc-line hover:border-aqc-red hover:bg-aqc-cream transition-colors">
            <div className="font-medium text-aqc-navy">{p.label}</div>
            <div className="text-xs text-aqc-muted mt-0.5 font-mono">/{p.slug}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PageForm({ slug, onBack }) {
  const [content, setContent] = useState({ hero: {}, cta: {} });
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const doc = await getPage(slug);
        setContent({ hero: doc?.content?.hero || {}, cta: doc?.content?.cta || {} });
      } catch {}
      finally { setBusy(false); }
    })();
  }, [slug]);

  const setHero = (k) => (e) => setContent((c) => ({ ...c, hero: { ...c.hero, [k]: e.target.value } }));
  const setCta = (k) => (e) => setContent((c) => ({ ...c, cta: { ...c.cta, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      await updatePage(slug, content);
      toast({ title: 'Page saved', description: 'Changes are live on the public site.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  if (busy) return <div className="p-6 text-sm text-aqc-muted">Loading…</div>;

  const isHome = slug === 'home';

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-aqc-navy"><ArrowLeft size={14} /> Back to pages</button>
        <span className="text-xs text-aqc-muted font-mono">/{slug}</span>
      </div>
      <h2 className="font-serif text-2xl text-aqc-navy">Hero section</h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div><Label>Eyebrow (small caps text)</Label><Input value={content.hero.eyebrow || ''} onChange={setHero('eyebrow')} className="mt-1.5" /></div>
        <div><Label>Title</Label><Input value={content.hero.title || ''} onChange={setHero('title')} className="mt-1.5" /></div>
        <div><Label>Italic accent (2nd line)</Label><Input value={content.hero.italic || ''} onChange={setHero('italic')} className="mt-1.5" /></div>
        <div className="sm:col-span-2"><Label>Subtitle</Label><Textarea rows={3} value={content.hero.subtitle || ''} onChange={setHero('subtitle')} className="mt-1.5" /></div>
        {isHome && (
          <>
            <div><Label>Primary CTA text</Label><Input value={content.hero.cta_primary || ''} onChange={setHero('cta_primary')} className="mt-1.5" /></div>
            <div><Label>Secondary CTA text</Label><Input value={content.hero.cta_secondary || ''} onChange={setHero('cta_secondary')} className="mt-1.5" /></div>
          </>
        )}
      </div>

      {isHome && (
        <div className="mt-8">
          <h2 className="font-serif text-2xl text-aqc-navy">Bottom CTA banner</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div><Label>Eyebrow</Label><Input value={content.cta.eyebrow || ''} onChange={setCta('eyebrow')} className="mt-1.5" /></div>
            <div><Label>Button text</Label><Input value={content.cta.button || ''} onChange={setCta('button')} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Title</Label><Input value={content.cta.title || ''} onChange={setCta('title')} className="mt-1.5" /></div>
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white gap-1.5"><Save size={14} /> {saving ? 'Saving…' : 'Save page'}</Button>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
      </div>
    </div>
  );
}
